import { AsyncPipe } from '@angular/common';
import { Component, inject, Input, OnInit } from '@angular/core';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { SideNavComponent } from './side-nav.component';
import { AppAuthService, TranslationService } from './common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { PRIMARY_OUTLET, Router, RouterModule, UrlTree } from '@angular/router';
import { MatDividerModule } from '@angular/material/divider';
import { Company, CompanySite } from './model';
import { AppMode, AppState } from './common/app-state';
import { MatListModule } from '@angular/material/list';
import { InfoTextBoxComponent, wikiUrl } from './common/info-text-box.component';
import { ServerVersionService } from './server-version.service';
import { NavigationService } from './common/navigation.service';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { LanguageSelectComponent } from './language-select/language-select.component';
import { TranslateModule } from '@ngx-translate/core';
import { CanaryComponent } from './common/canary.component';
import { BreakpointObserver } from '@angular/cdk/layout';
import { signal } from '@angular/core';

type FeatureType = 'risk' | 'eudr' | 'ppwr';

const FEATURE_PROPERTY: Record<
  FeatureType,
  keyof Pick<Company, 'enableRiskTool' | 'enableEudrTool' | 'enablePpwrTool'>
> = {
  risk: 'enableRiskTool',
  eudr: 'enableEudrTool',
  ppwr: 'enablePpwrTool',
};

@Component({
  selector: 'app-top-nav[sidenav]',
  imports: [
    AsyncPipe,
    RouterModule,
    MatToolbarModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatTooltipModule,
    MatDividerModule,
    MatListModule,
    MatSlideToggleModule,
    LanguageSelectComponent,
    TranslateModule,
    CanaryComponent,
  ],
  templateUrl: 'top-nav.component.html',
  styleUrls: ['top-nav.component.scss'],
})
export class TopNavComponent implements OnInit {
  readonly authService = inject(AppAuthService);
  readonly appState = inject(AppState);
  private readonly router = inject(Router);
  private readonly navigationService = inject(NavigationService);
  private readonly serverVersionService = inject(ServerVersionService);
  private readonly translation = inject(TranslationService);
  readonly isCompact = signal(false);

  @Input() sidenav!: SideNavComponent;
  companies!: Company[];
  selection?: { company: Company; site?: CompanySite };
  version!: string;
  testEnv?: string;
  showE2eLanguageSwitcher = false;
  currentAction?: string;
  mode: AppMode = AppMode.DEFAULT;

  async ngOnInit() {
    this.serverVersionService.subscribe(({ client }) => (this.version = client));
    this.appState.companySite$.subscribe((companySite) => (this.selection = companySite));
    // AppState loads the list on every user$ emission — the same trigger this component used to
    // fetch it again on its own (a second api/company/all per page load).
    this.appState.companies$.subscribe((companies) => (this.companies = companies));
    this.navigationService.currentRoute$.subscribe((route) => (this.currentAction = route?.action));
    this.appState.mode$.subscribe((mode) => (this.mode = mode));
    const { hostname } = window.location;
    this.testEnv = hostname === 'app.supplycanvas.com' ? undefined : hostname.split('.')[0];
    this.showE2eLanguageSwitcher = localStorage.getItem('e2e-screenshot-language-switcher') === 'true';
  }

  constructor(private breakpointObserver: BreakpointObserver) {
    this.breakpointObserver.observe('(max-width: 1156px)').subscribe((result) => this.isCompact.set(result.matches));
  }

  openHelp = () => InfoTextBoxComponent.openHelp(wikiUrl + '/home', this.translation.language);

  preselectCompanySite(company: Company, site: CompanySite) {
    this.appState.selectCompanySite(company, site);
  }

  switchCompany = (company: Company, site: CompanySite) =>
    this.buildUrlForSelection(company.id, site.id, !!company.enableEudrTool) ??
    (this.currentAction
      ? ['/company', company.id, 'site', site.id, this.currentAction]
      : ['/company', company.id, 'site', site.id]);

  // app#294: the site is left out only for a company KNOWN to have exactly one site — an unknown
  // count keeps it, so a site is never silently hidden.
  siteShown = (company: Company, site?: CompanySite, siteCount = company.sites?.length) => !!site && siteCount !== 1;

  // The count comes from the companies list — the same source the menu renders — because the
  // resolved selection company need not carry its sites.
  get selectionSiteShown() {
    if (!this.selection) return false;
    const { company, site } = this.selection;
    const listed = this.companies?.find((c) => c.id === company.id);
    return this.siteShown(company, site, (listed ?? company).sites?.length);
  }

  get selectionTitle() {
    if (!this.selection) return '';
    const { company, site } = this.selection;
    return site ? `${company.name} - ${site.name}` : company.name;
  }

  isFeatureDisabled = (type: FeatureType) => {
    const property = FEATURE_PROPERTY[type];
    return !this.selection?.company[property];
  };

  isFeatureHidden = (type: FeatureType) =>
    !!this.selection?.company.hideDisabledFeatures && this.isFeatureDisabled(type);

  protected readonly AppMode = AppMode;

  private buildUrlForSelection(companyId: string, siteId: string, hasEudrAccess: boolean): UrlTree | undefined {
    const currentTree = this.router.parseUrl(this.router.url);
    const primarySegments = currentTree.root.children[PRIMARY_OUTLET]?.segments.map((segment) => segment.path) ?? [];
    const companyIndex = primarySegments.indexOf('company');
    if (companyIndex === -1) {
      return undefined;
    }

    const tracesIndex = primarySegments.indexOf('traces-eudr');
    if (tracesIndex !== -1) {
      const tracesSuffix = primarySegments.slice(tracesIndex + 1);
      if (tracesSuffix.length === 1) {
        return this.router.createUrlTree(['/company', companyId, 'site', siteId, 'traces-eudr'], {
          queryParams: currentTree.queryParams,
          fragment: currentTree.fragment ?? undefined,
        });
      }
    }

    if (this.shouldResetToSafeList(primarySegments)) {
      const commands = this.safeListCommandsForSelection(companyId, siteId, primarySegments);
      return this.router.createUrlTree(commands);
    }

    const nextSegments = [...primarySegments];
    if (nextSegments[companyIndex + 1]) {
      nextSegments[companyIndex + 1] = companyId;
    } else {
      nextSegments.splice(companyIndex + 1, 0, companyId);
    }

    const siteIndex = nextSegments.indexOf('site');
    if (siteIndex === -1) {
      nextSegments.splice(companyIndex + 2, 0, 'site', siteId);
    } else if (nextSegments[siteIndex + 1]) {
      nextSegments[siteIndex + 1] = siteId;
    } else {
      nextSegments.splice(siteIndex + 1, 0, siteId);
    }

    if (hasEudrAccess) {
      const tracesIndex = nextSegments.indexOf('traces-eudr');
      if (tracesIndex !== -1 && nextSegments[tracesIndex + 1] === 'contact') {
        nextSegments.splice(tracesIndex + 1, 1);
      }
    }

    const commands = nextSegments.map((segment, index) => (index === 0 ? `/${segment}` : segment));

    return this.router.createUrlTree(commands, {
      queryParams: currentTree.queryParams,
      fragment: currentTree.fragment ?? undefined,
    });
  }

  private shouldResetToSafeList(segments: string[]): boolean {
    return (
      this.isDetailRoute(segments, 'chain', ['create']) ||
      this.isDetailRoute(segments, 'report', ['create']) ||
      this.isDetailRoute(segments, 'evaluation', ['create']) ||
      this.isPpwrEvaluationDetailRoute(segments) ||
      this.isDetailRoute(segments, 'archive', ['create']) ||
      this.isManageEditRoute(segments)
    );
  }

  private isDetailRoute(segments: string[], marker: string, allowedListPaths: string[]): boolean {
    const idx = segments.indexOf(marker);
    if (idx === -1) {
      return false;
    }
    const next = segments[idx + 1];
    if (!next) {
      return false;
    }
    return !allowedListPaths.includes(next);
  }

  private isPpwrEvaluationDetailRoute(segments: string[]): boolean {
    const ppwrIndex = segments.indexOf('ppwr');
    if (ppwrIndex === -1 || segments[ppwrIndex + 1] !== 'evaluation') {
      return false;
    }
    const next = segments[ppwrIndex + 2];
    return !!next && next !== 'create';
  }

  private safeListCommandsForSelection(companyId: string, siteId: string, currentSegments: string[]): any[] {
    if (this.isManageEditRoute(currentSegments)) {
      return ['/company', companyId, 'site', siteId, 'manage'];
    }
    const chainIndex = currentSegments.indexOf('chain');
    if (chainIndex !== -1) {
      return ['/company', companyId, 'site', siteId, 'chain'];
    }
    const ppwrIndex = currentSegments.indexOf('ppwr');
    if (ppwrIndex !== -1 && currentSegments[ppwrIndex + 1] === 'evaluation') {
      return ['/company', companyId, 'site', siteId, 'ppwr', 'evaluation'];
    }
    const evaluationIndex = currentSegments.indexOf('evaluation');
    if (evaluationIndex !== -1) {
      return ['/company', companyId, 'site', siteId, 'evaluation'];
    }
    const archiveIndex = currentSegments.indexOf('archive');
    if (archiveIndex !== -1) {
      return ['/company', companyId, 'site', siteId, 'archive'];
    }
    const tracesIndex = currentSegments.indexOf('traces-eudr');
    if (tracesIndex !== -1) {
      const tracesSuffix = currentSegments.slice(tracesIndex + 1);
      return ['/company', companyId, 'site', siteId, 'traces-eudr', ...tracesSuffix];
    }
    return ['/company', companyId, 'site', siteId];
  }

  private isManageEditRoute(segments: string[]): boolean {
    const manageIndex = segments.indexOf('manage');
    if (manageIndex === -1) {
      return false;
    }
    const next = segments[manageIndex + 1];
    return next === 'update' || next === 'update-site';
  }
}
